import { API_BASE_URL, BASE_URL } from '@synergy/config';
import { FormattedMessage } from 'react-intl';
import { ReactNode } from 'react';
import { StarIcon } from '@synergy/theme';
import { UserRole } from '@synergy/models';
import { connect } from 'react-redux';
import { hasRole, padZeroLeft } from '@synergy/utils';
import urljoin from 'url-join';

import { AppState } from '../../../../store/models/app-state';
import { BaseAutocompleteSuggestionType } from '../../autocomplete-list/pool-autocomplete-list';
import { Customer } from '../../../models/customer';
import { CustomerApi } from '../../../api/customer-api';
import { PoolAutocompleteListAction } from '../pool-autocomplete-list-action';
import { PoolAutocompleteListFooter } from '../pool-autocomplete-list-footer';
import { PoolFormAutocompleteList } from '../../abstract/form-autocomplete-list';
import { ROUTES } from '../../../../features/sidebar-menu/navigation-drawer/routes';
import { User } from '../../../models/user';

interface PartialCustomer extends BaseAutocompleteSuggestionType {
    name: string;
    isMain?: ReactNode;
}

interface PoolCustomerAutocompleteListProps {
    user: User;
    isLocked?: boolean;
}

class PoolCustomerAutocompleteListBase extends PoolFormAutocompleteList<
    Customer,
    PartialCustomer,
    PoolCustomerAutocompleteListProps
> {
    private actionRoles = [UserRole.Admin, UserRole.SuperAdmin, UserRole.Sales];
    private api = new CustomerApi(API_BASE_URL);
    private apiAbortController?: AbortController;
    private searchParams = new URLSearchParams({ limit: '40' });

    public footer = hasRole(this.actionRoles, this.props.user) && (
        <PoolAutocompleteListFooter
            text="app.navigation.customers"
            url={urljoin(BASE_URL, ROUTES.contactsAndOrganizations.customers.new)}
        />
    );

    public label = (<FormattedMessage id="app.label.customer" />);

    public componentDidMount() {
        this.setState({ items: [] });
    }

    public getSuggestionValue = (suggestion: PartialCustomer) => suggestion && suggestion.name;

    public async handleValueChanged(search: string) {
        this.searchParams.set('search', search);

        if (this.props.isLocked !== undefined && this.props.isLocked !== null) {
            this.searchParams.set('isLocked', `${this.props.isLocked}`);
        }

        this.apiAbortController?.abort();
        this.apiAbortController = new AbortController();
        const response = await this.api.cget(this.searchParams, this.apiAbortController.signal);
        if (!response.success && response.requestCanceled) {
            return;
        }
        const items = response.success === true ? response.result.items : [];
        this.setState({ items });
    }

    public getPartial = (items: Customer[]): PartialCustomer[] =>
        items.map(customer => {
            const editLink = urljoin(
                BASE_URL,
                ROUTES.contactsAndOrganizations.customers.detail.replace(':id', customer.id.toString())
            );
            return {
                id: padZeroLeft(customer.id, 5),
                isMain: customer.isMain && <StarIcon sx={{ verticalAlign: 'middle', fontSize: 20 }} />,
                name: customer.name,
                tags: customer.getTags(30),
                actions: <PoolAutocompleteListAction url={editLink} allowedRoles={this.actionRoles} />,
            };
        });
}

const mapStateToProps = (state: AppState) => ({
    user: state.user,
});

export const PoolCustomerAutocompleteList = connect(mapStateToProps, {})(PoolCustomerAutocompleteListBase);
